import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import { IPickListItem } from './models/pick-list-item.interface';
import { IPickListSettings } from './models/pick-list-settings.interface';

@Component({
  selector: 'sgt-pick-list',
  templateUrl: './pick-list.component.html',
  styleUrls: ['./pick-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PickListComponent {
  @Input() items: IPickListItem[] = [];
  @Input() settings: IPickListSettings;
  @Output() itemsChange = new EventEmitter<IPickListItem[]>();

  sourceSearchTerm = '';
  targetSearchTerm = '';
  private filteredSourceItems: IPickListItem[] = [];
  private filteredTargetItems: IPickListItem[] = [];

  private readonly selectedSourceItems = new Set<number>();
  private readonly selectedTargetItems = new Set<number>();

  /**
   * Retorna itens não selecionados (origem), filtrados pela busca se houver
   *
   * @returns Array de itens disponíveis para seleção
   */
  get sourceItems(): IPickListItem[] {
    const items = this.items?.filter(item => !item?.isSelected) ?? [];

    if (!this.sourceSearchTerm?.trim()) {
      this.filteredSourceItems = items;
      return items;
    }

    this.filteredSourceItems = items.filter(item =>
      item?.label?.toLowerCase().includes(this.sourceSearchTerm.toLowerCase().trim())
    );

    return this.filteredSourceItems;
  }

  /**
   * Retorna itens selecionados (destino), filtrados pela busca se houver
   *
   * @returns Array de itens já selecionados
   */
  get selectedItems(): IPickListItem[] {
    const items = this.items?.filter(item => item?.isSelected) ?? [];

    if (!this.targetSearchTerm?.trim()) {
      this.filteredTargetItems = items;
      return items;
    }

    this.filteredTargetItems = items.filter(item =>
      item?.label?.toLowerCase().includes(this.targetSearchTerm.toLowerCase().trim())
    );

    return this.filteredTargetItems;
  }

  /**
   * TrackBy function para melhorar performance da lista da origem
   *
   * @param index Índice do item
   * @param item Item da lista
   * @returns Identificador único para o item
   */
  trackBySourceItem(index: number, item: IPickListItem): number {
    return item?.id ?? index;
  }

  /**
   * TrackBy function para melhorar performance da lista do destino
   *
   * @param index Índice do item
   * @param item Item da lista
   * @returns Identificador único para o item
   */
  trackByTargetItem(index: number, item: IPickListItem): number {
    return item?.id ?? index;
  };

  /**
   * Alterna a seleção de um item na origem
   *
   * @param index Índice do item a ser alternado (relativo à lista filtrada)
   */
  toggleSourceSelection(index: number): void {
    if (this.isDisabled || !this.isValidIndex(index, this.sourceItems)) {
      return;
    }

    // Encontra o índice real do item na lista original baseado no item filtrado
    const selectedItem = this.sourceItems[index];
    const originalIndex = this.getOriginalSourceIndex(selectedItem);

    if (originalIndex !== null) {
      this.toggleItemSelection(this.selectedSourceItems, originalIndex);
    }
  }

  /**
   * Move um item específico da origem para o destino (usado no double-click)
   *
   * @param index Índice do item a ser movido
   */
  moveSourceItemToTarget(index: number): void {
    if (this.isDisabled || !this.isValidIndex(index, this.sourceItems)) {
      return;
    }

    const item = this.sourceItems[index];
    const shouldClearFilter = !!this.sourceSearchTerm && this.sourceItems.length === 1;
    if (item) {
      item.isSelected = true;
      this.selectedSourceItems.clear();
      if (shouldClearFilter) {
        this.clearSourceSearch();
      }
      this.emitItemsChange();
    }
  }

  /**
   * Alterna a seleção de um item no destino
   *
   * @param index Índice do item a ser alternado (relativo à lista filtrada)
   */
  toggleTargetSelection(index: number): void {
    if (this.isDisabled || !this.isValidIndex(index, this.selectedItems)) {
      return;
    }

    // Encontra o índice real do item na lista original baseado no item filtrado
    const selectedItem = this.selectedItems[index];
    const originalIndex = this.getOriginalTargetIndex(selectedItem);

    if (originalIndex !== null) {
      this.toggleItemSelection(this.selectedTargetItems, originalIndex);
    }
  }

  /**
   * Move um item específico do destino para a origem (usado no double-click)
   *
   * @param index Índice do item a ser movido
   */
  moveTargetItemToSource(index: number): void {
    if (this.isDisabled || !this.isValidIndex(index, this.selectedItems)) {
      return;
    }

    const item = this.selectedItems[index];
    const shouldClearFilter = !!this.targetSearchTerm && this.selectedItems.length === 1;
    if (item) {
      item.isSelected = false;
      this.selectedTargetItems.clear();
      if (shouldClearFilter) {
        this.clearTargetSearch();
      }
      this.emitItemsChange();
    }
  }

  /**
   * Move itens selecionados da origem para o destino
   */
  moveToTarget(): void {
    if (this.isDisabled || this.selectedSourceItems.size === 0) {
      return;
    }

    const sourceItems = this.sourceItems;
    this.updateItemsSelection(sourceItems, this.selectedSourceItems, true);
    this.selectedSourceItems.clear();
    this.clearSourceSearch();
    this.emitItemsChange();
  }

  /**
   * Move itens selecionados do destino para a origem
   */
  moveToSource(): void {
    if (this.isDisabled || this.selectedTargetItems.size === 0) {
      return;
    }

    const targetItems = this.selectedItems;
    this.updateItemsSelection(targetItems, this.selectedTargetItems, false);
    this.selectedTargetItems.clear();
    this.clearTargetSearch();
    this.emitItemsChange();
  }

  /**
   * Move todos os itens da origem para o destino
   */
  moveAllToTarget(): void {
    if (this.isDisabled || this.sourceItems.length === 0) {
      return;
    }

    this.items?.forEach(item => {
      if (item && !item.isSelected) {
        item.isSelected = true;
      }
    });

    this.selectedSourceItems.clear();
    this.clearSourceSearch();
    this.emitItemsChange();
  }

  /**
   * Move todos os itens do destino para a origem
   */
  moveAllToSource(): void {
    if (this.isDisabled || this.selectedItems.length === 0) {
      return;
    }

    this.items?.forEach(item => {
      if (item && item.isSelected) {
        item.isSelected = false;
      }
    });

    this.selectedTargetItems.clear();
    this.clearTargetSearch();
    this.emitItemsChange();
  }

  /**
   * Verifica se o componente está desabilitado
   *
   * @returns true se estiver desabilitado, false caso contrário
   */
  private get isDisabled(): boolean {
    return this.settings?.isDisabled ?? false;
  }

  /**
   * Verifica se um item da lista da origem está selecionado
   *
   * @param index Índice do item (relativo à lista filtrada)
   * @returns true se estiver selecionado, false caso contrário
   */
  isSourceItemSelected(index: number): boolean {
    const item = this.sourceItems[index];
    if (!item) {
      return false;
    }

    const originalIndex = this.getOriginalSourceIndex(item);
    return originalIndex !== null && this.selectedSourceItems.has(originalIndex);
  }

  /**
   * Verifica se um item da lista do destino está selecionado
   *
   * @param index Índice do item (relativo à lista filtrada)
   * @returns true se estiver selecionado, false caso contrário
   */
  isTargetItemSelected(index: number): boolean {
    const item = this.selectedItems[index];
    if (!item) {
      return false;
    }

    const originalIndex = this.getOriginalTargetIndex(item);
    return originalIndex !== null && this.selectedTargetItems.has(originalIndex);
  }

  /**
   * Verifica se existem itens selecionados na origem
   *
   * @returns true se existem itens selecionados, false caso contrário
   */
  hasSelectedSourceItems(): boolean {
    return this.selectedSourceItems.size > 0;
  }

  /**
   * Verifica se existem itens selecionados no destino
   *
   * @returns true se existem itens selecionados, false caso contrário
   */
  hasSelectedTargetItems(): boolean {
    return this.selectedTargetItems.size > 0;
  }

  /**
   * Alterna a seleção de um item no conjunto especificado
   *
   * @param selectedSet Conjunto de índices selecionados
   * @param index Índice a ser alternado
   */
  private toggleItemSelection(selectedSet: Set<number>, index: number): void {
    if (selectedSet.has(index)) {
      selectedSet.delete(index);
    } else {
      selectedSet.add(index);
    }
  }

  /**
   * Atualiza a seleção de múltiplos itens
   *
   * @param items Array de itens a serem atualizados
   * @param selectedIndices Conjunto de índices selecionados
   * @param isSelected Novo estado de seleção
   */
  private updateItemsSelection(
    items: IPickListItem[],
    selectedIndices: Set<number>,
    isSelected: boolean
  ): void {
    selectedIndices.forEach(index => {
      const item = items[index];
      if (item) {
        item.isSelected = isSelected;
      }
    });
  }

  /**
   * Valida se o índice é válido para o array especificado
   *
   * @param index Índice a ser validado
   * @param array Array para validação
   * @returns true se o índice for válido, false caso contrário
   */
  private isValidIndex<T>(index: number, array: T[]): boolean {
    return index >= 0 && index < array.length && array[index] != null;
  }

  /**
   * Emite mudança dos itens selecionados
   */
  private emitItemsChange(): void {
    this.itemsChange.emit([...this.selectedItems]);
  }

  /**
   * Limpa o termo de busca da origem
   */
  clearSourceSearch(): void {
    this.sourceSearchTerm = '';
    this.selectedSourceItems.clear();
  }

  /**
   * Limpa o termo de busca do destino
   */
  clearTargetSearch(): void {
    this.targetSearchTerm = '';
    this.selectedTargetItems.clear();
  }

  /**
   * Encontra o índice original de um item na lista de origem não filtrada
   *
   * @param item Item a ser localizado
   * @returns Índice original ou null se não encontrado
   */
  private getOriginalSourceIndex(item: IPickListItem): number {
    if (!item || !this.items) {
      return null;
    }

    const originalSourceItems = this.items.filter(originalItem => !originalItem?.isSelected);
    return originalSourceItems.findIndex(originalItem =>
      originalItem?.id === item.id || originalItem === item
    );
  }

  /**
   * Encontra o índice original de um item na lista de destino não filtrada
   *
   * @param item Item a ser localizado
   * @returns Índice original ou null se não encontrado
   */
  private getOriginalTargetIndex(item: IPickListItem): number {
    if (!item || !this.items) {
      return null;
    }

    const originalTargetItems = this.items.filter(originalItem => originalItem?.isSelected);
    return originalTargetItems.findIndex(originalItem =>
      originalItem?.id === item.id || originalItem === item
    );
  }
}
